import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom'; // 需求 R144：備註彈窗以 createPortal 掛到 document.body（需求 R99 結論）
import { useNavigate, Link } from 'react-router-dom';
import Header from '../components/Common/Header';
// 需求 R117（PM 2026-08-18 拍板 Q8=A；R3 PM 追加「四頁排列對齊客戶管理」）：
// 分頁列（含跳頁框）改用共用元件，本頁原本自寫的 renderPageButtons 一併移除。
import Pagination from '../components/Common/Pagination';
import './QuotesPage.css';
// 需求 R159（PM 2026-09-01）：報價單列表的單據層級「$」。
// tooltip 沿用 需求 R148 的同一句常數（Q6=A 單一真相），樣式沿用既有 .price-alert-dot
// （Q6／AC6：不新開第二份樣式、不修改 styles/sensitivePriceAlert.css）。
// 判定本身在後端（Q1=A），前端只讀 quote.hasSensitivePriceAlert 這個布林。
import { SENSITIVE_PRICE_ALERT_TITLE } from '../utils/sensitivePriceAlert';
import '../styles/sensitivePriceAlert.css';

const API = process.env.REACT_APP_API_URL || '/api';

const STATUS_MAP = {
  draft:    { label: '草稿',     className: 'status-badge-draft' },
  pending:  { label: '待審核',   className: 'status-badge-info' },
  approved: { label: '已核准',   className: 'status-badge-success' },
  rejected: { label: '退回修改', className: 'status-badge-danger' },
};

const COMPANY_BADGE = {
  '皇聚': 'company-huangji',
  '法羅': 'company-faluo',
  '維騰': 'company-mingxuan',
};

/*
 * 需求 R144（PM 2026-08-29 拍板 Q1-Q10）：建立者篩選的名單來源。
 * 名單＝「有權限建立報價單的用戶」，而 quote.create 權限只給 manager 與 sales
 * （後端 middleware/permissionMiddleware.js 的 rolePermissions:29-41、前端
 *   utils/permissions.js 的 ROLE_PERMISSIONS 兩邊一致；purchasing（採購）不在名單內）。
 * 這條過濾式與建立頁「聯絡人 (業務)」下拉完全相同（QuoteCreatePage.js 的 users 過濾），刻意保持一致。
 * Q8=B：**全列，含停用帳號**（isActive === false 也要留著）——舊報價單的建立者可能已離職停用，
 *       濾掉會讓那些單再也篩不出來。因此這裡只濾 role，不濾 isActive。
 * Q9=A：排序沿用 GET /api/users 現行的 createdAt 由新到舊（userController.getUsers 的
 *       .sort({ createdAt: -1 })），前端不再自行排序。
 */
const QUOTE_CREATOR_ROLES = ['manager', 'sales'];

/*
 * 需求 R154（PM 2026-09-01 拍板 Q1~Q6）：
 * 「客戶搜尋報價單，再回到上一頁希望是停留在同樣的搜尋結果，非清空」。
 * 根因＝本頁所有列表狀態都是元件內 useState，離開列表 → unmount → 條件全滅，返回時重新
 * mount 就變成「沒有任何條件」的全部報價單。解法沿用 需求 R43 在產品管理已驗證過的
 * sessionStorage 往返快照（PM Q2=A 與 需求 R43 的 D1 原則一致）：
 *   ‧ RETURN_ARMED_KEY 只在「自列表點進該報價單的詳情／編輯頁」時被設為 '1'；
 *   ‧ 列表 mount 時惰性讀出快照當各 state 初始值，並在 mount 後**立即**清除上膛旗標，
 *     所以只有「列表→詳情/編輯→返回列表」這一次往返會還原（AC10）；
 *   ‧ 從側邊選單重新進入、或在列表按 F5（皆未 arm）＝乾淨的預設清單（AC8／AC9）。
 * key 與產品管理的 productsList* 完全分開，兩頁互不干擾（AC15）。
 * Q3=B：上膛旗標只在「列表 mount」時清除，所以「列表→編輯頁→儲存後自動到詳情頁→回到列表」
 *       這種中途繞路仍會還原（AC4）——中間那站不是列表，不會把旗標吃掉。
 */
const RETURN_ARMED_KEY = 'quotesListReturnArmed';
const LIST_SNAPSHOT_KEY = 'quotesListSnapshot';

const QuotesPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [token, setToken] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [summaryCounts, setSummaryCounts] = useState({ draft: 0, pending: 0, approved: 0 });

  // 需求 R154：若本次 mount 是「自詳情／編輯頁返回列表」（已被 arm），讀出離開前存下的
  // 篩選／分頁快照作為下列各 state 的初始值。純讀無副作用，StrictMode 重複呼叫也安全；
  // sessionStorage 不可用（無痕／被停用）或 JSON 壞掉時回 null＝退回乾淨清單，不讓列表整頁報錯（AC16）。
  const [bootSnapshot] = useState(() => {
    try {
      if (sessionStorage.getItem(RETURN_ARMED_KEY) !== '1') return null;
      const raw = sessionStorage.getItem(LIST_SNAPSHOT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  // 需求 R154 Q1=A：五個篩選條件＋頁碼＋每頁筆數全部還原。
  const [page, setPage] = useState(() => bootSnapshot?.page ?? 1);
  const [pageSize, setPageSize] = useState(() => bootSnapshot?.pageSize ?? 25);
  const [keyword, setKeyword] = useState(() => bootSnapshot?.keyword ?? '');
  const [statusFilter, setStatusFilter] = useState(() => bootSnapshot?.statusFilter ?? '');
  const [companyFilter, setCompanyFilter] = useState(() => bootSnapshot?.companyFilter ?? '');
  const [companyOptions, setCompanyOptions] = useState([]);
  // 需求 R144：建立者篩選（Q10=A 單選下拉，預設空字串＝「全部建立者」，比照公司抬頭）
  const [createdByFilter, setCreatedByFilter] = useState(() => bootSnapshot?.createdByFilter ?? '');
  const [creatorOptions, setCreatorOptions] = useState([]);
  // 需求 R144：操作欄「眼睛」鈕開啟的備註彈窗；存的是整筆 quote（備註已在列表回應裡，不必再打 API）
  const [notesQuote, setNotesQuote] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => bootSnapshot?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => bootSnapshot?.dateTo ?? '');
  // 需求 R169（PM 2026-09-12 拍板 Q1~Q5）：進階篩選第二列「單品搜尋」——
  // 輸入產品中文名稱，列出所有「有報過此商品」的報價單。Q5=A 一併納入 需求 R154 往返快照。
  const [productNameFilter, setProductNameFilter] = useState(() => bootSnapshot?.productNameFilter ?? '');
  const [loading, setLoading] = useState(false);
  const [copyMode, setCopyMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [copying, setCopying] = useState(false);
  // 需求 R64（PM 2026-07-31）：新增刪除報價單功能，流程比照「複製報價單」，權限限主管。
  const [deleteMode, setDeleteMode] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isManager = currentUser?.role === 'manager';
  // 複製／刪除兩種模式共用同一套勾選框（勾選欄、全選、單列勾選）
  const selectMode = copyMode || deleteMode;

  useEffect(() => {
    const t = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!t || !user._id) { navigate('/login'); return; }
    setCurrentUser(user);
    setToken(t);

    // Fetch companies for filter
    fetch(`${API}/companies`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.json())
      .then(d => { if (Array.isArray(d)) setCompanyOptions(d); })
      .catch(() => {});

    // 需求 R144：建立者篩選名單。GET /api/users 只需 protect（userRoutes.js:6），
    // 不限主管，因此 sales 也拿得到名單，篩選欄對所有能進本頁的角色都可用。
    fetch(`${API}/users`, { headers: { Authorization: `Bearer ${t}` } })
      .then(r => r.json())
      .then(d => {
        const list = Array.isArray(d) ? d : (d.users || []);
        setCreatorOptions(list.filter(u => QUOTE_CREATOR_ROLES.includes(u.role)));
      })
      .catch(() => {});
  }, [navigate]);

  // 需求 R154（Q2=A）：列表 mount 後立刻清除上膛旗標——確保只有「離開列表→返回列表」這一次
  // 往返會還原；之後在列表上重新整理（AC9）、或從上方選單重新進入報價單列表（AC8，皆未再 arm）
  // 都會得到乾淨清單。快照本體刻意不刪，下次 arm 時會整份覆寫。
  useEffect(() => {
    try { sessionStorage.removeItem(RETURN_ARMED_KEY); } catch { /* sessionStorage 不可用時略過 */ }
  }, []);

  // 需求 R154：點「報價單編號」（→詳情頁）或操作欄「檢視／編輯」（→編輯頁）離開列表前，
  // 存下目前的五個篩選條件＋頁碼＋每頁筆數並 arm；返回列表（頁內返回鈕、取消彈窗、刪除後導回、
  // 瀏覽器上一頁 AC5）時 mount 會讀出此快照還原。
  // 一次性操作狀態（copyMode／deleteMode／selectedIds／notesQuote）**刻意不存**（AC11）：
  // 返回後應該是乾淨未進入模式、無勾選、彈窗未開。
  // try/catch 靜默略過（AC16）：無痕視窗下 sessionStorage 可能丟例外，這裡絕不能擋住導頁。
  const persistListStateForReturn = useCallback(() => {
    try {
      const snapshot = {
        keyword, dateFrom, dateTo, createdByFilter, statusFilter, companyFilter,
        // 需求 R169 Q5=A：單品搜尋條件與其他五個條件一樣，往返時保留
        productNameFilter,
        page, pageSize,
      };
      sessionStorage.setItem(LIST_SNAPSHOT_KEY, JSON.stringify(snapshot));
      sessionStorage.setItem(RETURN_ARMED_KEY, '1');
    } catch {
      /* sessionStorage 不可用時略過，不影響導頁 */
    }
  }, [keyword, dateFrom, dateTo, createdByFilter, statusFilter, companyFilter, productNameFilter, page, pageSize]);

  const fetchQuotes = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize, sort: '-createdAt' });
      if (keyword) params.set('keyword', keyword);
      if (statusFilter) params.set('status', statusFilter);
      if (companyFilter) params.set('companyHeader', companyFilter);
      // 需求 R144：建立者篩選（後端以頂層 AND 併入 filter，與其他四個篩選為交集）
      if (createdByFilter) params.set('createdBy', createdByFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      // 需求 R169：單品搜尋（後端 items.productName 全等比對、頂層 AND，與其他條件為交集）。
      // 純空白由後端 buildExactKeyword 去掉後視為未帶條件，這裡只擋完全空字串即可。
      if (productNameFilter) params.set('productName', productNameFilter);
      const res = await fetch(`${API}/quotes?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setQuotes(data.quotes || []);
      setTotal(data.total || 0);
      if (data.summaryCounts) setSummaryCounts(data.summaryCounts);
    } catch {
      setQuotes([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, keyword, statusFilter, companyFilter, createdByFilter, dateFrom, dateTo, productNameFilter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  // 需求 R154 Q4=A（「還原的頁碼已超出總頁數時（期間有報價單被刪）→ 自動回第 1 頁」）：
  // 還原的 page 可能大於目前的總頁數（離開期間該條件下的報價單被刪、或條件本身變了），
  // 後端會回空陣列，畫面就會停在一個沒有任何資料列的頁碼上。這裡在每次取回結果後校正一次。
  // 沒有無限迴圈風險：total 為 0 時 totalPagesNow 取 1，setPage(1) 在 page 已是 1 時 React 會
  // bail out 不重新 render；page 被改成 1 之後 fetchQuotes 依 deps 重打一次即為第 1 頁的結果。
  useEffect(() => {
    if (loading) return;
    const totalPagesNow = total > 0 ? Math.ceil(total / pageSize) : 1;
    if (page > totalPagesNow) setPage(1);
  }, [loading, total, page, pageSize]);

  // 需求 R144：備註彈窗以 Esc 關閉（遮罩點擊與右上 × 見下方 JSX）
  useEffect(() => {
    if (!notesQuote) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') setNotesQuote(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [notesQuote]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const enterCopyMode = () => {
    setCopyMode(true);
    setDeleteMode(false);
    setSelectedIds([]);
  };

  const cancelCopyMode = () => {
    setCopyMode(false);
    setSelectedIds([]);
  };

  // 需求 R64：刪除模式（與複製模式互斥，共用勾選框）
  const enterDeleteMode = () => {
    setDeleteMode(true);
    setCopyMode(false);
    setSelectedIds([]);
  };

  const cancelDeleteMode = () => {
    setDeleteMode(false);
    setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === quotes.length) setSelectedIds([]);
    else setSelectedIds(quotes.map((q) => q._id));
  };

  const handleConfirmCopy = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`確定要複製 ${selectedIds.length} 筆報價單？\n系統將依標號規則自動產生新單號（QT-YYYYMMDD-NNN）。`)) return;
    setCopying(true);
    try {
      const res = await fetch(`${API}/quotes/batch-duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || '複製失敗');
        return;
      }
      cancelCopyMode();
      fetchQuotes();
    } catch (err) {
      alert('複製失敗');
    } finally {
      setCopying(false);
    }
  };

  // 需求 R64：批量刪除報價單。報價單為硬刪除、無回收桶，故二次確認明確標示不可復原。
  const handleConfirmDelete = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`確定要刪除 ${selectedIds.length} 筆報價單？\n刪除後無法復原（系統沒有回收桶）。`)) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/quotes/batch-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.message || '刪除失敗');
        return;
      }
      alert(data.message || '已刪除');
      cancelDeleteMode();
      setPage(1);
      fetchQuotes();
    } catch (err) {
      alert('刪除失敗');
    } finally {
      setDeleting(false);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return `${d.getFullYear()}/${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
  };

  /*
   * 需求 R117 R3：原本這裡有一份自寫的 renderPageButtons（maxVisible=5 的視窗、
   * 「上一頁／下一頁」文字鈕、額外釘一顆末頁），與客戶管理的 ‹／› 排列不同。
   * 已整份移除，改由 components/Common/Pagination.js 依客戶管理的排列統一渲染。
   */

  if (!currentUser) return null;

  // 需求 R117 R3：顯示區間改由 <Pagination> 內部依 page/pageSize/total 計算，四頁同一條公式。

  return (
    <div className="quotes-page">
      <Header title="報價單列表" user={currentUser} onLogout={handleLogout} onUserUpdate={setCurrentUser} />

      <div className="container">
        {/* 統計摘要卡片 */}
        <section className="summary-cards">
          <div className="summary-card card-draft">
            <div className="summary-card-label">草稿</div>
            <div className="summary-card-value">{summaryCounts.draft}</div>
            <div className="summary-card-sub">待完成送審</div>
          </div>
          <div className="summary-card card-pending">
            <div className="summary-card-label">待審核</div>
            <div className="summary-card-value">{summaryCounts.pending}</div>
            <div className="summary-card-sub">等待主管審核</div>
          </div>
          <div className="summary-card card-approved">
            <div className="summary-card-label">已核准</div>
            <div className="summary-card-value">{summaryCounts.approved}</div>
            <div className="summary-card-sub">本月核准</div>
          </div>
        </section>

        {/* 篩選區 */}
        <section className="filter-section">
          <h2 className="filter-section-title">進階篩選</h2>
          <div className="filter-row">
            <div className="filter-group">
              <label>客戶名稱</label>
              {/*
                需求 R113 R2（PM 2026-08-21：「請將此功能延續到報價單列表中的
                『進階搜尋-客戶名稱』」）：本欄原本只比對報價單編號與 customerName 字串快照，
                現在後端（quoteController.getAll）會另外用客戶編號／簡稱／聯絡人／Email
                反查 Customer 再以 customer ref 命中報價單，可搜尋範圍與客戶管理一致，
                因此 placeholder 一併明列可輸入的內容（同 R1 兩頁「文案沒告知」的修法）。
              */}
              <input
                type="text"
                className="form-control"
                placeholder="搜尋報價單編號、客戶名稱、客戶編號、簡稱、聯絡人、Email..."
                value={keyword}
                onChange={e => { setKeyword(e.target.value); setPage(1); }}
              />
            </div>
            <div className="filter-group filter-group-date">
              <label>報價日期區間</label>
              <div className="date-range-row">
                <input
                  type="date"
                  className="form-control"
                  value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                />
                <span className="date-sep">～</span>
                <input
                  type="date"
                  className="form-control"
                  value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setPage(1); }}
                />
              </div>
            </div>
            {/*
              需求 R144（PM 2026-08-29 拍板 Q7=B「放在『報價單狀態』之前」、Q10=A 單選下拉）：
              建立者篩選。名單只含有 quote.create 權限的角色（QUOTE_CREATOR_ROLES），
              value 送 user._id（Quote.createdBy 是 ObjectId ref，不能送姓名）。
            */}
            <div className="filter-group filter-group-creator">
              <label>建立者</label>
              <select
                className="form-control"
                value={createdByFilter}
                onChange={e => { setCreatedByFilter(e.target.value); setPage(1); }}
              >
                <option value="">全部建立者</option>
                {creatorOptions.map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            {/* 需求 R98：狀態欄加專屬 class 供 QuotesPage.css 縮寬（最長選項只有「退回修改」4 字） */}
            <div className="filter-group filter-group-status">
              <label>報價單狀態</label>
              <select
                className="form-control"
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="">全部狀態</option>
                <option value="draft">草稿</option>
                <option value="pending">待審核</option>
                <option value="approved">已核准</option>
                <option value="rejected">退回修改</option>
              </select>
            </div>
            <div className="filter-group">
              <label>公司抬頭</label>
              <select
                className="form-control"
                value={companyFilter}
                onChange={e => { setCompanyFilter(e.target.value); setPage(1); }}
              >
                <option value="">全部公司</option>
                {companyOptions.map(c => (
                  <option key={c._id} value={c.label}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>
          {/*
            需求 R169（PM 2026-09-12 拍板）：「單品搜尋」——
            「希望在報價單列表中篩選商品，會列出所有有報過此商品的報價單」。
            Q3=C 放在**第二列**：獨立一個 .filter-row，不擠進上面五欄那一列（五欄在 1366 寬
                  已貼近換行門檻，見 QuotesPage.css 需求 R144 段的量測）。
            Q4=A label「單品搜尋」、placeholder「輸入產品中文名稱（需完全相符）」。
            Q1=A 全等比對／Q2=A 比對品項快照 items.productName——都在後端 quoteController.getAll。
          */}
          <div className="filter-row filter-row-product">
            <div className="filter-group filter-group-product">
              <label>單品搜尋</label>
              <input
                type="text"
                className="form-control"
                placeholder="輸入產品中文名稱（需完全相符）"
                value={productNameFilter}
                onChange={e => { setProductNameFilter(e.target.value); setPage(1); }}
              />
            </div>
          </div>
        </section>

        {/* 操作功能列 */}
        <section className="action-bar">
          <div className="action-group">
            <button className="btn btn-success btn-sm" onClick={() => navigate('/quotes/create')} disabled={selectMode}>
              + 建立報價單
            </button>
            {!copyMode ? (
              <button className="btn btn-sm btn-primary" onClick={enterCopyMode} disabled={deleteMode}>
                複製報價單
              </button>
            ) : (
              <>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={handleConfirmCopy}
                  disabled={!selectedIds.length || copying}
                >
                  {copying ? '複製中…' : `確認複製${selectedIds.length ? `（${selectedIds.length}）` : ''}`}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={cancelCopyMode} disabled={copying}>
                  取消
                </button>
              </>
            )}
            {/* 需求 R64（PM 2026-07-31）：刪除報價單，流程同「複製報價單」；僅主管看得到此按鈕，後端 canDeleteQuote 亦擋非主管。 */}
            {isManager && (!deleteMode ? (
              <button className="btn btn-sm btn-danger" onClick={enterDeleteMode} disabled={copyMode}>
                刪除報價單
              </button>
            ) : (
              <>
                <button
                  className="btn btn-sm btn-danger"
                  onClick={handleConfirmDelete}
                  disabled={!selectedIds.length || deleting}
                >
                  {deleting ? '刪除中…' : `確認刪除${selectedIds.length ? `（${selectedIds.length}）` : ''}`}
                </button>
                <button className="btn btn-sm btn-secondary" onClick={cancelDeleteMode} disabled={deleting}>
                  取消
                </button>
              </>
            ))}
          </div>
          <div className="result-count">
            共 <strong>{total}</strong> 筆報價單
          </div>
        </section>

        {/* 報價單表格 */}
        <section className="table-container">
          {loading ? (
            <div className="quotes-loading">載入中…</div>
          ) : quotes.length === 0 ? (
            <div className="quotes-empty-state">
              <div className="empty-icon">📋</div>
              <h2>尚無報價單</h2>
              {/* 需求 R144：新增的建立者篩選也要算進「有套用篩選」，否則篩到 0 筆會顯示「點擊『+ 建立報價單』開始建立」 */}
              {/* 需求 R169：單品搜尋同理（AC9） */}
              <p>{keyword || statusFilter || companyFilter || createdByFilter || dateFrom || dateTo || productNameFilter
                ? '沒有符合條件的報價單'
                : '點擊「+ 建立報價單」開始建立'}</p>
            </div>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr>
                    {selectMode && (
                      <th style={{ width: 40, textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          className="select-checkbox"
                          checked={selectedIds.length === quotes.length && quotes.length > 0}
                          onChange={toggleSelectAll}
                        />
                      </th>
                    )}
                    <th className="sortable">報價單編號</th>
                    <th className="sortable">報價日期 / 截止日期</th>
                    <th>客戶名稱</th>
                    <th style={{ textAlign: 'center' }}>報價幣別 / 匯率</th>
                    {/* 需求 R144 Q1=A：沿用同表「報價日期 / 截止日期」的半形斜線格式 */}
                    <th>建立者 / 負責業務</th>
                    <th>公司抬頭</th>
                    <th style={{ textAlign: 'center' }}>匯出狀態</th>
                    <th style={{ textAlign: 'center' }}>狀態</th>
                    {/* 需求 R144：操作欄由原本寫死的 80px 放寬到 120px，才放得下「編輯／檢視」＋眼睛鈕兩顆並排（AC12） */}
                    <th style={{ width: '120px', textAlign: 'center' }}>操作</th>
                    {/*
                      需求 R159（PM 2026-09-01）：「位置設定在操作欄位的右側」→ 本欄是最後一欄。
                      Q3=A 表頭留空白（欄寬只有 40px，放字會換行）；aria-label 只給輔助技術，畫面不出字。
                      Q4「不寫死欄寬」→ **客戶名稱欄一字未改**（原文的 307.5px 不落地），
                        只有本新欄依 Q5=A 固定 40px，其餘欄仍由瀏覽器自動分配。
                    */}
                    <th style={{ width: '40px', textAlign: 'center' }} aria-label="成本異動提醒" />
                  </tr>
                </thead>
                <tbody>
                  {quotes.map(q => {
                    const st = STATUS_MAP[q.status] || STATUS_MAP.draft;
                    const companyClass = COMPANY_BADGE[q.companyHeader] || '';
                    // 需求 R33：所有狀態（草稿 / 待審核 / 已審核 / 退回）皆改為導向 /quotes/:id/edit；
                    // - 草稿：完整可編輯；
                    // - 待審核：manager 與 sales 都可編輯、儲存、送審（PM 2026-05-22 修正條件）；
                    // - 已審核：進入編輯頁但所有欄位以 isLocked 鎖定為唯讀，不顯示寫入按鈕。
                    // 按鈕「文字」維持原規則：draft = 編輯，其他狀態 = 檢視（僅行為導向改變，視覺不變）。
                    //
                    // 需求 R125（PM 2026-08-20 追問「點『詳情』直接進編輯畫面，
                    // 是設定壞了，還是之前調整過設定？」）——答案是**後者**，而且不是主管專屬：
                    // 上面這條 需求 R33 規則對 manager / sales 一視同仁（本 map 內完全沒有讀
                    // user.role，也沒有讀 q.createdBy），所以任何角色點「操作」欄的按鈕都直接進
                    // /quotes/:id/edit，不會先停在詳情頁。這是 2026-05-22 PM 自己拍板的行為，非 bug。
                    // 2026-08-21 PM 拍板收尾：「這條導向**維持不變**，改成讓待審核的報價單進到編輯頁時
                    // 出現『審核退回』按鈕，主管權限同時保留『儲存並審核』。」
                    // → 本檔不動；實作在 QuoteEditPage.js（canReject / handleReject / 動作列退回鈕）。
                    // 主管現在走「列表 → 檢視（編輯頁）」也看得到退回鈕，不必再繞詳情頁。
                    // 詳情頁 QuoteDetailPage.js:247-256 的退回鈕維持存在，兩個入口同一套顯示條件。
                    // 可執行證據：tests/quoteListRowEntry.fixture.js
                    const isDraft = q.status === 'draft';
                    return (
                      <tr key={q._id}>
                        {selectMode && (
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              className="select-checkbox"
                              checked={selectedIds.includes(q._id)}
                              onChange={() => toggleSelect(q._id)}
                            />
                          </td>
                        )}
                        <td>
                          {/*
                            需求 R125：這顆「報價單編號」是報價單詳情頁 /quotes/:id 目前的**唯一入口**
                            （需求 R33 之後，右側「操作」欄一律導向編輯頁）。
                            視覺上它只有 hover 底線（QuotesPage.css:195-202），沒有按鈕外框、也沒有
                            「詳情」字樣，容易被誤認為純文字——這正是 PM 2026-08-20 回報「待審核單找不到
                            審核退回」的動線成因（退回鈕當時只存在於詳情頁）。
                            2026-08-21 PM 拍板的解法不是改這條動線，而是在編輯頁補上「審核退回」
                            （QuoteEditPage.js canReject / handleReject），故本 span 維持原樣不動。
                            PM 原文（2026-08-21 評論）：「使用者不會想要有不同路徑進到報價單…但是此做法
                            可以保留」——亦即「點編號進詳情頁」這條路**不刪、不改樣式、也不新增『詳情』鈕**，
                            只是不再是主管執行退回的必經之路。日後若有人想把它移除，請先回頭確認本決議。
                          */}
                          {/* 需求 R154（AC1／AC14）：離開列表前先存快照再導頁。className 與
                              navigate 目標一字未改，需求 R125 的動線與樣式決議不受影響。 */}
                          <span
                            className="quote-number"
                            onClick={() => { persistListStateForReturn(); navigate(`/quotes/${q._id}`); }}
                          >
                            {q.quoteNumber}
                          </span>
                        </td>
                        <td className="date-cell">
                          <div className="date-main">{formatDate(q.quoteDate || q.createdAt)}</div>
                          {q.expiryDate && (
                            <div className="date-sub">截止 {formatDate(q.expiryDate)}</div>
                          )}
                        </td>
                        <td>{q.customerName || q.customer?.name || '—'}</td>
                        <td className="currency-cell">
                          {q.currency ? (
                            <>
                              <div className="currency-main">{q.currency}</div>
                              {q.currency !== 'TWD' && Number.isFinite(Number(q.exchangeRate)) && (
                                <div className="currency-sub">{Number(q.exchangeRate).toFixed(3)}</div>
                              )}
                            </>
                          ) : '—'}
                        </td>
                        {/*
                          需求 R144（PM 2026-08-29 Q2=A 兩行、Q3=A 缺值顯示「—」）：
                          主行＝建立者（quote.createdBy，後端 create 時以 req.user._id 寫入），
                          副行＝負責業務（quote.contactPerson，即建立／編輯頁的「聯絡人 (業務)」）。
                          ⚠️ 兩者**不得互相 fallback**（AC3）：舊單 contactPerson 可能是 null，
                             拿 createdBy 頂上去會把「沒指定業務」謊報成「業務就是建立者」。
                          兩個名字都已由 getAll 的 populate 帶回，本欄不需要任何後端改動（AC5）。
                        */}
                        <td className="creator-cell">
                          <div className="creator-main">{q.createdBy?.name || '—'}</div>
                          <div className="creator-sub">{q.contactPerson?.name || '—'}</div>
                        </td>
                        <td>
                          {q.companyHeader
                            ? <span className={`company-badge ${companyClass}`}>{q.companyHeader}</span>
                            : '—'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {q.exported
                            ? <span className="export-badge exported">已列印/匯出</span>
                            : '—'}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`status-badge ${st.className}`}>{st.label}</span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div className="row-action-group">
                            {/* AC13：這顆的文字規則（草稿＝編輯、其餘＝檢視）與 to=/quotes/:id/edit 完全不動 */}
                            {/* 需求 R154（AC3／AC14）：第二條離開動線也要存快照。刻意**維持 <Link>**
                                （不改成 button），右鍵開新分頁／中鍵仍可用；onClick 只是加掛，
                                即使使用者是開新分頁而非同分頁導航，最多也只是多存一份快照，
                                因為快照要等「返回列表 mount」時才會被讀，且讀完立即解除上膛。 */}
                            <Link
                              className={`row-action-btn ${isDraft ? 'btn-edit' : 'btn-view'}`}
                              to={`/quotes/${q._id}/edit`}
                              onClick={persistListStateForReturn}
                            >
                              {isDraft ? '編輯' : '檢視'}
                            </Link>
                            {/*
                              需求 R144（PM 2026-08-29 Q4=A 彈窗、Q5=A 沒備註也照樣顯示且可點）：
                              「眼睛」＝檢視該報價單的備註。備註內容直接取自列表回應
                              （getAll 的 Quote.find(filter) 沒有 .select()，internalNotes 已隨列表整份回傳），
                              所以這裡**不再打一次 GET /api/quotes/:id**（AC8）。
                              圖示沿用專案既有的 👁 字元（QuoteCreatePage.js／QuoteEditPage.js 的品項資訊鈕），
                              不引入任何 icon 套件；外框沿用本頁的 .row-action-btn 才能與左邊的文字鈕等高。
                            */}
                            <button
                              type="button"
                              className="row-action-btn btn-notes"
                              title="檢視備註"
                              aria-label="檢視備註"
                              onClick={() => setNotesQuote(q)}
                            >
                              👁
                            </button>
                          </div>
                        </td>
                        {/*
                          需求 R159：單據層級的「$」——這張單「任一品項」符合 需求 R148 的判定就亮。
                          ‧ 判定完全在後端（Q1=A），前端不重算、也拿不到任何明細（AC8）。
                          ‧ 為 false 時**留空白**、不放「—」（AC2）：本欄的「—」會與其他欄的缺值符號混淆。
                          ‧ Q6=A 純提示：不掛 onClick、不可點，只有 title 提示（AC7）。
                        */}
                        <td style={{ textAlign: 'center' }}>
                          {q.hasSensitivePriceAlert && (
                            <span className="price-alert-dot" title={SENSITIVE_PRICE_ALERT_TITLE}>$</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/*
                分頁控制
                需求 R117 R3（PM 2026-08-18）：排列對齊客戶管理——每頁顯示（左）／頁碼＋跳頁框（中）／
                筆數資訊（右）。本頁原本是「筆數資訊 → 每頁顯示 → 頁碼」且頁碼是文字版「上一頁／下一頁」。
              */}
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                pageSize={pageSize}
                pageSizeOptions={[25, 50, 100]}
                onPageChange={setPage}
                onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
              />
            </>
          )}
        </section>
      </div>

      {/*
        需求 R144 備註彈窗（PM 2026-08-29 拍板 Q4=A 彈窗 / Q5=A 空備註顯示「尚無備註」/ Q6=A 唯讀）。
        依 需求 R99 結論一律 createPortal 到 document.body：留在 <td> 內會被 .table-container
        的 overflow 裁切、也會被表格與導覽列的 stacking context 蓋住（AC9）。
        ⚠️ 這裡讀的是 quote.internalNotes（需求 R143 新增的「備註」）。同一張報價單上另有兩個
           長得很像的欄位，都**不是**本票要的（AC7）：
             ‧ quote.transactionNotes → 交易條件，會進 PDF／Excel 匯出；
             ‧ items[].notes          → 品項層級備註。
        Q6=A 唯讀（AC11）：本彈窗沒有任何 input／textarea，也不發任何寫入請求；要改請進編輯頁。
      */}
      {notesQuote && ReactDOM.createPortal((
        <div className="quote-notes-overlay" onClick={() => setNotesQuote(null)}>
          <div
            className="quote-notes-modal"
            role="dialog"
            aria-modal="true"
            aria-label="報價單備註"
            onClick={e => e.stopPropagation()}
          >
            <div className="quote-notes-header">
              <h3 className="quote-notes-title">備註</h3>
              <button
                type="button"
                className="quote-notes-close"
                onClick={() => setNotesQuote(null)}
                aria-label="關閉"
              >
                ×
              </button>
            </div>
            <div className="quote-notes-meta">
              {notesQuote.quoteNumber}
              {notesQuote.customerName || notesQuote.customer?.name
                ? `｜${notesQuote.customerName || notesQuote.customer?.name}`
                : ''}
            </div>
            <div className="quote-notes-body">
              {(notesQuote.internalNotes || '').trim()
                ? <div className="quote-notes-text">{notesQuote.internalNotes}</div>
                : <div className="quote-notes-empty">尚無備註</div>}
            </div>
            <div className="quote-notes-footer">
              <span className="quote-notes-hint">此處僅供檢視，如需修改請至報價單編輯頁。</span>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => setNotesQuote(null)}>
                關閉
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
};

export default QuotesPage;
